import { useState, useEffect } from "react";
import AppLayout from "@/layouts/AppLayout";
import Sidebar from "@/components/Sidebar";
import ChatInputArea from "@/components/ChatInputArea";
import ChatOutput from "@/components/ChatOutput";
import { useChat } from "@/hooks/useChat";
import { BREAKPOINTS } from "@/constants/constants";
import toast from "react-hot-toast";

export default function Landing() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isLargeScreen, setIsLargeScreen] = useState(false);

    const {
        conversations,
        selectedConversationId,
        isLoading,
        createNewConversation,
        selectConversation,
        sendMessage,
        deleteConversation,
        uploadPdf,
        quota,
    } = useChat();

    // Handle screen size changes
    useEffect(() => {
        const checkScreenSize = () => {
            const large = window.innerWidth >= BREAKPOINTS.LARGE_SCREEN;
            setIsLargeScreen(large);

            // Auto-close sidebar when switching to large screen
            if (large && sidebarOpen) {
                setSidebarOpen(false);
            }
        };

        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        return () => window.removeEventListener("resize", checkScreenSize);
    }, [sidebarOpen]);

    // Get current conversation
    const currentConversation = conversations.find(
        (conv) => conv.id === selectedConversationId,
    );

    const handleNewConversation = () => {
        createNewConversation();
        // console.log("New conversation started");
    };

    const handleSelectConversation = (id: string) => {
        // console.log("Selected conversation:", id);
        selectConversation(id);
    };

    const handleSendMessage = async (message: string) => {
        await sendMessage(message);
    };

    const handleUploadPdf = async (file: File) => {
        await uploadPdf(file);
    };

    const handleInputError = (message: string) => {
        toast.error(message);
    };

    const handleToggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    return (
        <AppLayout
            title="FAIRLEGAL"
            sidebarOpen={sidebarOpen}
            onToggleSidebar={handleToggleSidebar}
        >
            <div className="flex h-full w-full gap-8">
                {/* Sidebar */}
                <Sidebar
                    conversations={conversations}
                    onNewConversation={handleNewConversation}
                    onSelectConversation={handleSelectConversation}
                    onDeleteConversation={deleteConversation}
                    selectedConversationId={selectedConversationId}
                    isOpen={sidebarOpen}
                    isLargeScreen={isLargeScreen}
                    onClose={() => setSidebarOpen(false)}
                />

                {/* Main Content Area - Three Columns */}
                <div className="flex-1 flex flex-col md:flex-row gap-8 relative">
                    {/* Floating Burger Menu Button - Only show below 1440px */}
                    {!isLargeScreen && (
                        <button
                            onClick={handleToggleSidebar}
                            className="absolute -top-12 -left-3 lg:-left-6 z-30 p-3 bg-white hover:bg-gray-100 rounded-full shadow-lg border border-gray-200 transition-all duration-200 hover:shadow-xl cursor-pointer"
                            aria-label="Toggle sidebar"
                        >
                            <svg
                                className="w-6 h-6 text-gray-700"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                {sidebarOpen ? (
                                    // X icon when sidebar is open
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                ) : (
                                    // Hamburger icon when sidebar is closed
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6h16M4 12h16M4 18h16"
                                    />
                                )}
                            </svg>
                        </button>
                    )}

                    {/* Middle Column - Input Area */}
                    <div className="flex-1 bg-gray-200 rounded-2xl relative">
                        <ChatInputArea
                            onSendMessage={handleSendMessage}
                            onUploadPdf={handleUploadPdf}
                            onInputError={handleInputError}
                            disabled={isLoading}
                            messages={currentConversation?.messages ?? []}
                            quota={quota}
                        />
                    </div>

                    {/* Third Column - Output Area */}
                    <div className="flex-1 bg-gray-200 rounded-2xl relative">
                        {currentConversation ? (
                            <ChatOutput
                                messages={currentConversation.messages}
                                isLoading={isLoading}
                            />
                        ) : (
                            <ChatOutput messages={[]} isLoading={false} />
                        )}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
